import { Dialog as ChakraDialog, Portal } from "@chakra-ui/react";
import { CloseButton } from "./close-button";
import { forwardRef } from "react";
import { useThemeConfig } from "../../hooks/useThemeConfig";
import { AutoHeight } from "../AutoHeight/AutoHeight";
import { Box } from "./box";
import { Flex } from "./flex";

/**
 * @typedef {object} DialogProps
 * @prop {import("react").ReactNode} children
 * @prop {import("@chakra-ui/react").Dialog.ContentProps["minHeight"]} [minHeight]
 * @prop {import("@chakra-ui/react").Dialog.ContentProps["maxWidth"]} [maxWidth]
 */
/**
 * @param {DialogProps & ChakraDialog.RootProps} props
 */
export function Dialog({ children, minHeight, maxWidth, ...otherProps }) {
  return (
    <DialogRoot {...otherProps}>
      <DialogContent minHeight={minHeight} maxWidth={maxWidth}>
        {children}
      </DialogContent>
    </DialogRoot>
  );
}

export const DialogContent = forwardRef(
  /**
   * @typedef {object} DialogContentProps
   * @prop {boolean} [portalled]
   * @prop {import("react").RefObject<HTMLElement>} [portalRef]
   * @prop {boolean} [backdrop]
   * @prop {React.ReactNode} children
   */
  /**
   * @param {DialogContentProps & import("@chakra-ui/react").Dialog.ContentProps} props
   */
  function DialogContent(props, ref) {
    const {
      children,
      portalled = true,
      portalRef,
      backdrop = true,
      ...rest
    } = props;

    return (
      <Portal disabled={!portalled} container={portalRef}>
        {backdrop && <ChakraDialog.Backdrop />}

        <ChakraDialog.Positioner>
          <ChakraDialog.Content ref={ref} {...rest} asChild={false}>
            {children}
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </Portal>
    );
  },
);

export const DialogCloseTrigger = forwardRef(
  /**
   * @param {ChakraDialog.CloseTriggerProps} props
   */
  function DialogCloseTrigger(props, ref) {
    return (
      <ChakraDialog.CloseTrigger
        position="absolute"
        top="2"
        insetEnd="2"
        {...props}
        asChild
      >
        <CloseButton size="sm" ref={ref}>
          {props.children}
        </CloseButton>
      </ChakraDialog.CloseTrigger>
    );
  },
);

/**
 * @typedef {ChakraDialog.RootProps} DialogRootProps
 */
/**
 * @param {DialogRootProps} props
 */
export function DialogRoot(props) {
  const { dialogRootProps } = useThemeConfig();

  return <ChakraDialog.Root {...dialogRootProps} {...props} />;
}

/**
 * @param {import("@chakra-ui/react").DialogHeaderProps} props
 */
export function DialogHeader(props) {
  const { dialogHeaderProps } = useThemeConfig();

  return <ChakraDialog.Header {...dialogHeaderProps} {...props} />;
}

/**
 * @param {import("@chakra-ui/react").DialogFooterProps} props
 */
export function DialogFooter(props) {
  const { dialogFooterProps } = useThemeConfig();

  return (
    <ChakraDialog.Footer flexWrap="wrap" {...dialogFooterProps} {...props} />
  );
}

/**
 * @param {import("@chakra-ui/react").DialogBodyProps & { autoHeight?: boolean;}} props
 */
export function DialogBody({
  children,
  autoHeight = true,
  px,
  py,
  pt,
  pb,
  ...rest
}) {
  return (
    <ChakraDialog.Body
      display={autoHeight ? undefined : "flex"}
      flexDirection={autoHeight ? undefined : "column"}
      px="0"
      py="0"
      {...rest}
    >
      {autoHeight ? (
        <AutoHeight>
          <Box py={py ? py : "16px"} px={px ? px : "24px"} pt={pt} pb={pb}>
            {children}
          </Box>
        </AutoHeight>
      ) : (
        <Flex
          flexDirection="column"
          flexGrow={1}
          py={py ? py : "16px"}
          px={px ? px : "24px"}
          pt={pt}
          pb={pb}
        >
          {children}
        </Flex>
      )}
    </ChakraDialog.Body>
  );
}

export const DialogBackdrop = ChakraDialog.Backdrop;
export const DialogTitle = ChakraDialog.Title;
export const DialogDescription = ChakraDialog.Description;
export const DialogTrigger = ChakraDialog.Trigger;

/**
 * @param {ChakraDialog.ActionTriggerProps} props
 */
export const DialogActionTrigger = (props) => {
  return (
    <ChakraDialog.ActionTrigger {...props} asChild={props.asChild ?? true} />
  );
};
